import FieldSection from 'types/transformation/enums/FieldSection'
import { MapNode } from 'types/transformation/types/nodes'
import { FIELD_SECTIONS } from 'types/transformation/types/sections'

import { sortAlphabeticalAsc } from 'utils/sortHelper'

export type MapField = MapNode['schema'][number]

// Keep the field's original index in `schema` so edits map back correctly.
export interface IndexedField {
  field: MapField
  index: number
}

export interface SectionGroup {
  key: FieldSection
  fields: IndexedField[]
}

export interface GroupedFields {
  sections: SectionGroup[]
  deprecated: IndexedField[]
}

const byName = (a: IndexedField, b: IndexedField) =>
  sortAlphabeticalAsc(a.field.name, b.field.name)

/**
 * Group the Output (MAP) node's fields for rendering:
 * - calculated fields are left out, the pipeline fills them
 * - deprecated fields go to their own group (deprecated trumps the section)
 * - remaining fields go to their `section` (fallback: standard_data)
 * - sections are returned in FIELD_SECTIONS order, empty ones omitted
 * - fields are sorted alphabetically (Swedish) within each group
 */
export function groupBySection(schema: MapField[]): GroupedFields {
  const deprecated: IndexedField[] = []
  const bySection = new Map<FieldSection, IndexedField[]>()

  schema.forEach((field, index) => {
    if (field.is_calculated) {
      return
    }

    if (field.deprecated) {
      deprecated.push({ field, index })

      return
    }

    const key = field.section ?? FieldSection.STANDARD_DATA
    const bucket = bySection.get(key)

    if (bucket) {
      bucket.push({ field, index })
    } else {
      bySection.set(key, [{ field, index }])
    }
  })

  const sections = FIELD_SECTIONS.map((definition) => ({
    key: definition.key,
    fields: (bySection.get(definition.key) ?? []).sort(byName)
  })).filter((group) => group.fields.length > 0)

  return { sections, deprecated: deprecated.sort(byName) }
}
